import { cn } from "../lib/utils";
import * as Icons from "lucide-react";

export function Icon({ name, className, ...props }) {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} {...props} />;
}

/* Subtle surface panel — soft tint, not a hard white card */
export function Panel({ className, children, tint = "white", hover = false, ...props }) {
  const tints = {
    white: "bg-white border-mim-line",
    soft: "bg-mim-soft border-mim-blue/10",
    ghost: "bg-white/60 border-white/70",
  };
  return (
    <div className={cn("border rounded-3xl p-5 sm:p-6 shadow-mim", tints[tint], hover && "hover-lift cursor-pointer", className)} {...props}>
      {children}
    </div>
  );
}

/* kept for backward-compat */
export function Card({ className, children, hover = false, ...props }) {
  return (
    <div className={cn("bg-white border border-mim-line rounded-3xl p-5 sm:p-6 shadow-mim transition-all duration-200",
      hover && "hover-lift cursor-pointer", className)} {...props}>
      {children}
    </div>
  );
}

export function Overline({ children, className }) {
  return <div className={cn("text-[11px] font-bold uppercase tracking-[0.15em] text-mim-muted", className)}>{children}</div>;
}

const pillStyles = {
  blue: "bg-mim-soft text-mim-blue border-mim-blue/20",
  green: "bg-mim-green/10 text-mim-green border-mim-green/20",
  purple: "bg-mim-purple/10 text-mim-purple border-mim-purple/20",
  yellow: "bg-mim-yellow/15 text-[#b07d13] border-mim-yellow/30",
  coral: "bg-mim-coral/10 text-mim-coral border-mim-coral/20",
  cyan: "bg-mim-cyan/10 text-mim-cyan border-mim-cyan/20",
  gray: "bg-slate-100 text-mim-muted border-slate-200",
  white: "bg-white/15 text-white border-white/25",
};
export function Pill({ tone = "blue", children, className, icon }) {
  return (
    <span className={cn("text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1.5 border", pillStyles[tone], className)}>
      {icon && <Icon name={icon} className="w-3 h-3" />}{children}
    </span>
  );
}

export function ProgressBar({ value = 0, color = "var(--mim-blue)", className, height = "h-2.5", track = "#E8EEF6" }) {
  return (
    <div className={cn("w-full rounded-full overflow-hidden", height, className)} style={{ background: track }}>
      <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: color }} />
    </div>
  );
}

export function ProgressRing({ value = 0, size = 92, stroke = 9, color = "#2477E8", track = "#E8EEF6", label, sublabel, labelClass }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, value) / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(0.16,1,0.3,1)" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("text-xl font-bold leading-none", labelClass || "text-mim-navy")}>{label ?? `${Math.round(value)}%`}</span>
        {sublabel && <span className={cn("text-[9px] font-bold uppercase tracking-widest mt-1", labelClass ? "opacity-70" : "text-mim-muted")}>{sublabel}</span>}
      </div>
    </div>
  );
}

export function Btn({ variant = "primary", size = "md", className, children, icon, iconRight, ...props }) {
  const styles = {
    primary: "bg-mim-blue hover:bg-mim-bluehover text-white shadow-md shadow-mim-blue/25",
    secondary: "bg-white hover:bg-mim-soft text-mim-navy border border-mim-line hover:border-mim-blue/40",
    ai: "bg-mim-purple hover:bg-mim-purple/90 text-white shadow-md shadow-mim-purple/25",
    green: "bg-mim-green hover:bg-mim-green/90 text-white shadow-md shadow-mim-green/25",
    ghost: "bg-transparent hover:bg-mim-soft text-mim-navy",
    coral: "bg-mim-coral hover:bg-mim-coral/90 text-white shadow-md",
    white: "bg-white text-mim-navy hover:bg-white/90 shadow-md",
    glass: "glass text-white border border-white/25 hover:bg-white/20",
  };
  const sizes = { sm: "px-3.5 py-2 text-xs rounded-xl", md: "px-5 py-2.5 text-sm rounded-xl", lg: "px-6 py-3.5 text-base rounded-2xl" };
  return (
    <button className={cn("font-semibold transition-all inline-flex items-center justify-center gap-2 active:scale-98 disabled:opacity-50 disabled:pointer-events-none", styles[variant], sizes[size], className)} {...props}>
      {icon && <Icon name={icon} className={size === "lg" ? "w-5 h-5" : "w-4 h-4"} />}
      {children}
      {iconRight && <Icon name={iconRight} className={size === "lg" ? "w-5 h-5" : "w-4 h-4"} />}
    </button>
  );
}

/* Colored metric tile — soft tinted background instead of plain white */
const toneMap = {
  blue: { bg: "bg-mim-soft", chip: "bg-mim-blue text-white", text: "text-mim-blue" },
  green: { bg: "bg-mim-green/8", chip: "bg-mim-green text-white", text: "text-mim-green" },
  purple: { bg: "bg-mim-purple/8", chip: "bg-mim-purple text-white", text: "text-mim-purple" },
  yellow: { bg: "bg-mim-yellow/12", chip: "bg-mim-yellow text-white", text: "text-[#b07d13]" },
  coral: { bg: "bg-mim-coral/8", chip: "bg-mim-coral text-white", text: "text-mim-coral" },
  cyan: { bg: "bg-mim-cyan/8", chip: "bg-mim-cyan text-white", text: "text-mim-cyan" },
};
export function Metric({ icon, label, value, tone = "blue", sub, className }) {
  const t = toneMap[tone];
  return (
    <div className={cn("rounded-3xl p-5 border border-transparent", t.bg, className)}>
      <div className={cn("w-10 h-10 rounded-2xl flex items-center justify-center mb-3 shadow-sm", t.chip)}>
        <Icon name={icon} className="w-5 h-5" />
      </div>
      <div className="text-2xl sm:text-[28px] font-bold text-mim-navy leading-none">{value}</div>
      <div className="text-xs font-semibold text-mim-muted mt-1.5">{label}</div>
      {sub && <div className={cn("text-[11px] mt-0.5 font-medium", t.text)}>{sub}</div>}
    </div>
  );
}
export { Metric as StatCard };

/* Subject gradient identity */
export const SUBJECT_GRADIENTS = {
  "#2477E8": "linear-gradient(135deg,#2f86f5,#1b5fc0)",
  "#20B982": "linear-gradient(135deg,#28c98f,#159669)",
  "#F3B83F": "linear-gradient(135deg,#f7c65e,#e09a1f)",
  "#19B9D8": "linear-gradient(135deg,#33c7e2,#0f97b3)",
  "#7657E8": "linear-gradient(135deg,#8a6df0,#5f3fd0)",
};
export const gradFor = (color) => SUBJECT_GRADIENTS[color] || "linear-gradient(135deg,#2f86f5,#1b5fc0)";

export function EmptyState({ icon = "Inbox", title, subtitle, action, tone = "blue" }) {
  const t = toneMap[tone];
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-6">
      <div className={cn("w-16 h-16 rounded-3xl flex items-center justify-center mb-4", t.bg)}>
        <Icon name={icon} className={cn("w-7 h-7", t.text)} />
      </div>
      <h4 className="text-lg font-bold text-mim-navy">{title}</h4>
      {subtitle && <p className="text-sm text-mim-muted mt-1 max-w-sm">{subtitle}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }) { return <div className={cn("shimmer rounded-2xl", className)} />; }

export function PageLoader() {
  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-6xl mx-auto">
      <Skeleton className="h-40 w-full rounded-3xl" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-3xl" />)}</div>
      <Skeleton className="h-64 w-full rounded-3xl" />
    </div>
  );
}

export function SectionTitle({ title, action, icon, sub }) {
  return (
    <div className="flex items-end justify-between mb-4 gap-3">
      <div>
        <h3 className="text-xl font-bold text-mim-navy flex items-center gap-2">
          {icon && <Icon name={icon} className="w-5 h-5 text-mim-blue" />}{title}
        </h3>
        {sub && <p className="text-sm text-mim-muted mt-0.5">{sub}</p>}
      </div>
      {action}
    </div>
  );
}
